import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, AlertTriangle, Coffee, Car, Download, Pencil, Trash2 } from "lucide-react";
import { formatLocalISODate } from "../lib/dates";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const HOURS = Array.from({length: 14}, (_, i) => 8 + i); // 8am–9pm
const HOUR_HEIGHT = 52;

function startOfWeek(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0,0,0,0);
  return date;
}

function defaultWeekStart() {
  const today = new Date();
  if (today.getDay() === 0) {
    const d = addDays(today, 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  return startOfWeek(today);
}

function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate()+n); return c; }
function iso(d) { return formatLocalISODate(d); }

const BLANK_FORM = { client_id: "", date: "", start_time: "15:00", end_time: "17:00", notes: "" };

export default function SchedulePage() {
  const [weekStart, setWeekStart] = useState(defaultWeekStart());
  const [therapists, setTherapists] = useState([]);
  const [clients, setClients] = useState([]);
  const [therapistId, setTherapistId] = useState("");
  const [sessions, setSessions] = useState([]);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(BLANK_FORM);
  const [creating, setCreating] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editSession, setEditSession] = useState(null); // full session object
  const [editForm, setEditForm] = useState(BLANK_FORM);
  const [editTherapistId, setEditTherapistId] = useState("");
  const [saving, setSaving] = useState(false);

  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const loadBase = async () => {
    const [t, c] = await Promise.all([api.get("/therapists"), api.get("/clients")]);
    setTherapists(t.data);
    setClients(c.data);
    if (!therapistId && t.data.length) setTherapistId(t.data[0].id);
  };

  const loadSessions = async () => {
    if (!therapistId) return;
    const { data } = await api.get("/sessions", { params: { therapist_id: therapistId, start_date: iso(weekStart), end_date: iso(weekEnd) } });
    setSessions(data);
  };

  useEffect(() => { loadBase(); }, []);
  useEffect(() => { loadSessions(); }, [therapistId, weekStart]);

  // ---- Create ----
  const openCreate = (dayIdx) => {
    setCreateForm({ ...BLANK_FORM, client_id: clients[0]?.id || "", date: iso(addDays(weekStart, dayIdx)) });
    setCreateOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/sessions", { ...createForm, therapist_id: therapistId });
      toast.success("Session created");
      setCreateOpen(false);
      loadSessions();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === "object" && d?.errors) ? d.errors.join(" ") : (formatApiError(d) || err.message));
    } finally { setCreating(false); }
  };

  // ---- Edit ----
  const openEdit = (s) => {
    setEditSession(s);
    setEditForm({ client_id: s.client_id, date: s.date, start_time: s.start_time, end_time: s.end_time, notes: s.notes || "" });
    setEditTherapistId(s.therapist_id);
    setEditOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/sessions/${editSession.id}`, { ...editForm, therapist_id: editTherapistId });
      toast.success("Session updated");
      setEditOpen(false);
      loadSessions();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === "object" && d?.errors) ? d.errors.join(" ") : (formatApiError(d) || err.message));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this session?")) return;
    await api.delete(`/sessions/${editSession.id}`);
    toast.success("Session deleted");
    setEditOpen(false);
    loadSessions();
  };

  // ---- Grid layout ----
  const blocksByDay = useMemo(() => {
    const map = {};
    for (let i=0;i<7;i++) map[i] = [];
    sessions.forEach(s => {
      const [y, mo, dy] = s.date.split("-").map(Number);
      const d = new Date(y, mo - 1, dy);
      const diff = Math.floor((d - weekStart) / (1000*60*60*24));
      if (diff >= 0 && diff < 7) map[diff].push(s);
    });
    return map;
  }, [sessions, weekStart]);

  const clientName = (id) => clients.find(c=>c.id===id)?.name || "Client";

  const DISCIPLINE_COLOR = {
    bt: { bg: "#E5EBE8", border: "#274f38", text: "#18231E" },
    program_manager: { bg: "#FAF0E8", border: "#B07C60", text: "#4a3020" },
    bcba: { bg: "#E8EFF5", border: "#5b7fa6", text: "#1e3a52" },
  };

  return (
    <div>
      <PageHeader
        title="Schedule builder"
        subtitle="Click a session to edit it. Click + on a day header to add a new session."
      />
      <div className="px-10 pb-10 space-y-6">
        {/* Toolbar */}
        <div className="bg-surface border border-soft rounded-lg p-4 flex items-center gap-4 card-shadow">
          <select data-testid="schedule-therapist-select" value={therapistId} onChange={(e)=>setTherapistId(e.target.value)} className="h-10 px-3 rounded-md border border-soft bg-white text-sm min-w-[240px]">
            {therapists.map(t => <option key={t.id} value={t.id}>{t.name} ({t.therapist_role})</option>)}
          </select>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              data-testid="export-ics-btn"
              variant="outline" size="sm"
              onClick={async () => {
                try {
                  const resp = await api.get(`/sessions/export.ics?therapist_id=${therapistId}`, { responseType: "blob" });
                  const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: "text/calendar" }));
                  const a = document.createElement("a");
                  a.href = blobUrl;
                  a.download = "schedule.ics";
                  a.click();
                  URL.revokeObjectURL(blobUrl);
                } catch {}
              }}
              disabled={!therapistId}
            >
              <Download size={14} className="mr-1"/> Export .ics
            </Button>
            <Button variant="outline" size="sm" data-testid="week-prev" onClick={()=>setWeekStart(addDays(weekStart,-7))}><ChevronLeft size={14}/></Button>
            <div className="font-mono text-sm px-2" data-testid="week-label">{iso(weekStart)} → {iso(weekEnd)}</div>
            <Button variant="outline" size="sm" data-testid="week-next" onClick={()=>setWeekStart(addDays(weekStart,7))}><ChevronRight size={14}/></Button>
            <Button size="sm" data-testid="today-btn" onClick={()=>setWeekStart(defaultWeekStart())}>Today</Button>
          </div>
        </div>

        {/* Calendar grid */}
        <div className="bg-surface border border-soft rounded-lg card-shadow overflow-hidden" data-testid="schedule-grid">
          <div className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] border-b border-soft bg-muted-soft">
            <div className="px-3 py-3 text-xs uppercase tracking-[0.18em] text-muted-ohana font-semibold">Time</div>
            {DAYS.map((d, i) => (
              <div key={i} className="px-3 py-3 text-xs uppercase tracking-[0.18em] text-muted-ohana font-semibold border-l border-soft flex items-center justify-between">
                <div>
                  <div>{d}</div>
                  <div className="font-mono text-[11px] normal-case tracking-normal text-[#586960] mt-0.5">{iso(addDays(weekStart, i))}</div>
                </div>
                <button data-testid={`add-session-day-${i}`} onClick={()=>openCreate(i)} className="ml-1 text-[#274f38] hover:bg-white rounded-md p-1"><Plus size={14}/></button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] relative">
            <div>
              {HOURS.map(h => (
                <div key={h} style={{height:HOUR_HEIGHT}} className="border-b border-soft px-3 pt-1 text-[11px] font-mono text-muted-ohana">{String(h).padStart(2,"0")}:00</div>
              ))}
            </div>
            {DAYS.map((_, dayIdx) => (
              <div key={dayIdx} className="relative border-l border-soft" style={{height: HOURS.length * HOUR_HEIGHT}}>
                {HOURS.map(h => <div key={h} style={{height:HOUR_HEIGHT}} className="border-b border-[#EEEAE0]"/>)}
                {blocksByDay[dayIdx]?.map(s => {
                  const [sh, sm] = s.start_time.split(":").map(Number);
                  const [eh, em] = s.end_time.split(":").map(Number);
                  const top = ((sh - HOURS[0]) + sm/60) * HOUR_HEIGHT;
                  const height = ((eh - sh) + (em - sm)/60) * HOUR_HEIGHT;
                  if (top < 0 || top > HOURS.length*HOUR_HEIGHT) return null;
                  const c = DISCIPLINE_COLOR[s.discipline] || DISCIPLINE_COLOR.bt;
                  return (
                    <div
                      key={s.id}
                      data-testid={`sch-block-${s.id}`}
                      onClick={() => openEdit(s)}
                      style={{ top, height, left: 6, right: 6, backgroundColor: c.bg, borderLeftColor: c.border, color: c.text }}
                      className="absolute border-l-4 rounded-md p-2 text-xs cursor-pointer hover:brightness-95 transition-all group"
                    >
                      <div className="flex items-start justify-between gap-1">
                        <div className="font-semibold truncate">{clientName(s.client_id)}</div>
                        <Pencil size={10} className="shrink-0 opacity-0 group-hover:opacity-50 mt-0.5"/>
                      </div>
                      <div className="font-mono text-[10px] opacity-70">{s.start_time}–{s.end_time}</div>
                      {s.discipline && <div className="text-[9px] font-bold uppercase tracking-wider opacity-50 mt-0.5">{s.discipline === "program_manager" ? "PM" : s.discipline?.toUpperCase()}</div>}
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {s.rest_break_required && <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0 rounded-full bg-[#FDF4E7] text-[#B07C60]"><AlertTriangle size={8}/> rest</span>}
                        {s.lunch_break_required && <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0 rounded-full bg-[#FDF4E7] text-[#B07C60]"><Coffee size={8}/> lunch</span>}
                        {s.travel_minutes_before > 0 && <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0 rounded-full bg-white/60 text-[#586960]"><Car size={8}/> {s.travel_minutes_before}m</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---- Create dialog ---- */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="schedule-dialog">
          <DialogHeader><DialogTitle style={{fontFamily:"Outfit"}}>New session</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 pt-2">
            <div>
              <Label>Client</Label>
              <select required value={createForm.client_id} onChange={e=>setCreateForm({...createForm,client_id:e.target.value})} className="w-full h-10 px-3 rounded-md border border-soft bg-white text-sm">
                <option value="">— select —</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><Label>Date</Label><Input type="date" required value={createForm.date} onChange={e=>setCreateForm({...createForm,date:e.target.value})}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label><Input type="time" required value={createForm.start_time} onChange={e=>setCreateForm({...createForm,start_time:e.target.value})}/></div>
              <div><Label>End</Label><Input type="time" required value={createForm.end_time} onChange={e=>setCreateForm({...createForm,end_time:e.target.value})}/></div>
            </div>
            <div><Label>Notes</Label><Input value={createForm.notes} onChange={e=>setCreateForm({...createForm,notes:e.target.value})}/></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={()=>setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} className="bg-primary-ohana hover:bg-[#1E3D2B] text-white">{creating?"Saving…":"Create session"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---- Edit dialog ---- */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="edit-session-dialog">
          <DialogHeader>
            <DialogTitle style={{fontFamily:"Outfit"}}>Edit session</DialogTitle>
          </DialogHeader>
          {editSession && (
            <form onSubmit={handleSaveEdit} className="space-y-4 pt-2">
              <div>
                <Label>Therapist</Label>
                <select value={editTherapistId} onChange={e=>setEditTherapistId(e.target.value)} className="w-full h-10 px-3 rounded-md border border-soft bg-white text-sm">
                  {therapists.map(t=><option key={t.id} value={t.id}>{t.name} ({t.therapist_role})</option>)}
                </select>
              </div>
              <div>
                <Label>Client</Label>
                <select required value={editForm.client_id} onChange={e=>setEditForm({...editForm,client_id:e.target.value})} className="w-full h-10 px-3 rounded-md border border-soft bg-white text-sm">
                  <option value="">— select —</option>
                  {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div><Label>Date</Label><Input type="date" required value={editForm.date} onChange={e=>setEditForm({...editForm,date:e.target.value})}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start</Label><Input type="time" required value={editForm.start_time} onChange={e=>setEditForm({...editForm,start_time:e.target.value})}/></div>
                <div><Label>End</Label><Input type="time" required value={editForm.end_time} onChange={e=>setEditForm({...editForm,end_time:e.target.value})}/></div>
              </div>
              <div><Label>Notes</Label><Input value={editForm.notes} onChange={e=>setEditForm({...editForm,notes:e.target.value})}/></div>
              <div className="flex items-center justify-between pt-2">
                <Button type="button" variant="outline" onClick={handleDelete} className="text-red-600 border-red-200 hover:bg-red-50 gap-1.5">
                  <Trash2 size={14}/> Delete
                </Button>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={()=>setEditOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={saving} className="bg-primary-ohana hover:bg-[#1E3D2B] text-white">{saving?"Saving…":"Save changes"}</Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
